import type { ReactNode } from "react";
import { type HTMLAttributes, useCallback } from "react";

import { MdDelete, MdSend } from "react-icons/md";
import * as Transform from "../../utility/transforms";
import * as Validator from "../../utility/validators";
import { useForm } from "../form";
import { useEditor } from "../generic-editor";

import { Input, type InputProps } from "./input";
import { Select } from "./select";
import { TextArea } from "./textarea";

export function Email(props: InputProps) {
  return <Input {...props} type="email" inboundTransform={Transform.lowerCase} validator={Validator.email} placeholder="Your Email Address "/>;
}

export function Password(props: InputProps) {
  return <Input {...props} type="password" validator={Validator.password} placeholder="Password"/>;
}

export function PasswordConfirm({ siblingName, ...props }: InputProps & { siblingName: string }) {
  const form = useForm();
  return <Input {...props} type="password" validator={Validator.passwordConfirm(siblingName, form)} placeholder="Confirm Password"/>;
}

export function FullName(props: InputProps) {
  return <Input {...props} type="name" validator={Validator.name} placeholder="Your Name"/>;
}

export function ProductName(props: InputProps) {
  return <Input {...props} type="name" validator={Validator.productName} placeholder="Product Name"/>;
}

export function CompanyName(props: InputProps) {
  return <Input {...props} type="company" validator={Validator.company} placeholder="Company Name"/>;
}

export function WebsiteURI(props: InputProps) {
  return <Input {...props} type="uri" validator={Validator.uri} placeholder="Website URI"/>;
}

export function TaxID(props: InputProps) {
  return <Input {...props} type="taxId" validator={Validator.taxId} placeholder="Tax ID"/>;
}

export function Phone(props: InputProps) {
  return <Input {...props} type="phone" placeholder="Phone Number"/>;
}

export function Domain(props: InputProps) {
  return <Input {...props} type="domain" validator={Validator.domain} inboundTransform={Transform.lowerCase} placeholder="myDomain.com"/>;
}

export function CategoryName(props: InputProps) {
  return <Input {...props} type="categoryName" validator={Validator.categoryName} placeholder="Product Category"/>;
}

export function GenericName(props: InputProps) {
  return <Input {...props} type="categoryName" validator={Validator.categoryName} placeholder="Name"/>;
}

export function ObjectCode(props: InputProps) {
  return <Input {...props} type="code" placeholder="Code"/>;
}

export function Tags(props: InputProps) {
  return (
    <Input {...props} type="tags" placeholder="Tags" inboundTransform={Transform.array2delimited(",")} outboundTransform={Transform.delimited2array(",")}/>
  );
}

export function DateField(props: InputProps) {
  return <Input {...props} type="date" placeholder="Date"/>; // InboundTransform={ Transform.date2ymd } outboundTransform={ Transform.ymd2date }/>;
}

export function Hidden(props: InputProps) {
  return <Input {...props} type="hidden" style={{ display: "none" }}/>;
}

export function UUID(props: InputProps) {
  return <Input {...props} readOnly type="string"/>;
}

export function String(props: InputProps) {
  return <Input {...props} type="string"/>;
}

export function Number(props: InputProps) {
  return <Input {...props} type="number" placeholder="0.00"/>;
}

export function Integer(props: InputProps) {
  return <Input {...props} type="number" placeholder="0"/>;
}

export function TimeInterval(props: InputProps) {
  return <Input {...props} type="text" placeholder="00:00:00"/>;
}

export function TimeOfDay(props: InputProps) {
  return <Input {...props} type="time" placeholder=""/>;
}

export function Timestamp(props: InputProps) {
  return <Input {...props} type="datetime" placeholder=""/>;
}

export function Timezone(props: InputProps) {
  return <Input {...props} type="timezone" placeholder="Timezone Name"/>;
}

export function Currency(props: InputProps) {
  return <Input {...props} prefix="$" type="string" placeholder="0.00"/>;
}

export function Submit({ className = "", ...props }: HTMLAttributes<HTMLButtonElement>) {
  const form = useForm();
  const enabled = form?.valid && !form.disabled && form.dirty;

  return (
    <button
      type="submit"
      className={`w-auto py-1.5 px-3 rounded text-white text-base font-semibold transition-colors flex items-center gap-2 ${
        enabled ? "bg-[#ee6e73] hover:bg-[#e05560] cursor-pointer" : "bg-[#ccc] text-[#888] cursor-not-allowed"
      } ${className}`}
      {...props}
    >
      <MdSend/>
      {props.children}
    </button>
  );
}

export function Delete({ className = "", ...props }: HTMLAttributes<HTMLButtonElement>) {
  const form = useForm();
  const editor = useEditor();

  const handleClick = useCallback(() => editor?.deleteRecord(), [editor]);
  const enabled = !form?.disabled;

  return (
    <button
      type="button"
      className={`w-auto py-1.5 px-3 rounded text-white text-base font-semibold transition-colors flex items-center gap-2 ${
        enabled ? "bg-[#ee6e73] hover:bg-[#e05560] cursor-pointer" : "bg-[#ccc] text-[#888] cursor-not-allowed"
      } ${className}`}
      onClick={handleClick}
      {...props}
    >
      <MdDelete/>
      {props.children}
    </button>
  );
}

export function JSONEditor(props: Parameters<typeof TextArea>["0"]) {
  return <TextArea {...props} inboundTransform={Transform.jsonToString} outboundTransform={Transform.stringToJSON} validator={Validator.json}/>;
}

export function Markdown(props: Parameters<typeof TextArea>["0"]) {
  return <TextArea {...props} />;
}

export function BooleanSelect({ children, ...props }: Parameters<typeof Select>["0"] & { children: ReactNode }) {
  return (
    <Select {...props} inboundTransform={Transform.bool2string} outboundTransform={Transform.string2bool}>
      {children}
    </Select>
  );
}

export { AutoComplete } from "./autocomplete";
export { FieldSet as Set, FieldSet } from "./fieldset";
export { Input } from "./input";
export { Option, Select } from "./select";
export { TextArea } from "./textarea";
export { Validity } from "./validity";
